import React from 'react'
import './user-role-policy-editor.scss'
import { Well } from '@pushly/aqe/lib/components'
import { IAccessPolicy } from '@halo/interfaces/access-policy'
import { Table, Input, Flex } from 'antd'
import AccessPolicyExpandedRowView from '@halo/components/access-policy-expanded-row-view/access-policy-expanded-row-view'
import { classnames } from '@halo/_utils/classnames'
import { usePrevious } from '@halo/hooks/use-previous'
import * as deepEqual from 'react-fast-compare'
import { TableRowEntityDisplay } from '@halo/components/table-row-entity-display/table-row-entity-display'

interface IUserRolePolicyEditorProps {
    className?: string
    title?: string
    value?: number[]
    defaultValue?: number[]
    onChange: (policyIds: number[], policies: IAccessPolicy[]) => any
    options: IAccessPolicy[]
    actions?: React.ReactNode
}

const UserRolePolicyEditor: React.FunctionComponent<IUserRolePolicyEditorProps> = (props) => {
    const prevOptions = usePrevious(props.options)
    const [visibleOptions, setVisibleOptions] = React.useState<IAccessPolicy[]>(props.options)
    const [value, setValue] = React.useState<number[]>(props.value ?? props.defaultValue ?? [])
    const [searchValue, setSearchValue] = React.useState<string>('')

    const searchAccessPolicies = React.useCallback(
        async (search: string) => {
            setSearchValue(search)

            if (!search.trim()) {
                setVisibleOptions(props.options)
            } else {
                const searchRgx = new RegExp(`${search}`, 'i')
                setVisibleOptions(
                    props.options.filter(
                        (policy) =>
                            searchRgx.test(policy.name) || (policy.description && searchRgx.test(policy.description)),
                    ),
                )
            }
        },
        [JSON.stringify(props.options)],
    )

    React.useEffect(() => {
        const prevOptionIds = prevOptions.map((ap) => ap.id)
        prevOptionIds.sort()
        const currOptionIds = props.options.map((ap) => ap.id)
        currOptionIds.sort()

        if (!deepEqual(prevOptionIds, currOptionIds)) {
            searchAccessPolicies(searchValue)
        }
    }, [JSON.stringify(props.options)])

    const tableHeader = (
        <Flex gap={8} justify="space-between" align="center">
            <Flex flex={1} gap={8} align="center">
                <Input.Search
                    style={{ width: 300 }}
                    size="middle"
                    placeholder="Search ..."
                    onSearch={searchAccessPolicies}
                    onChange={(ev) => searchAccessPolicies(ev.target.value)}
                />
            </Flex>
        </Flex>
    )

    return (
        <Well
            className={classnames('user-role-policy-editor', 'type-2', props.className)}
            title={props.title ?? 'Access Policies'}
            actions={props.actions}
            hideFooter={true}
        >
            <Table<IAccessPolicy>
                title={() => tableHeader}
                size="small"
                bordered={false}
                dataSource={visibleOptions}
                rowKey={(row) => row.id}
                pagination={false}
                rowSelection={{
                    selectedRowKeys: props.value ?? value,
                    hideSelectAll: true,
                    onChange: (_selectedKeys: React.Key[], selectedRows: IAccessPolicy[]) => {
                        const nextSelectedIds = selectedRows.map((ap) => ap.id)
                        setValue(nextSelectedIds)
                        props.onChange(nextSelectedIds, selectedRows)
                    },
                }}
                expandedRowRender={(policy) => <AccessPolicyExpandedRowView policy={policy} />}
            >
                <Table.Column
                    className="policy"
                    title="Policy"
                    dataIndex="name"
                    render={(_, policy: IAccessPolicy) => (
                        <TableRowEntityDisplay title={policy.name} status={policy.description} />
                    )}
                />
            </Table>
        </Well>
    )
}

export default UserRolePolicyEditor
